import type { ReactNode } from 'react';

import Text from '@cartwhl/ui/base/TextNew';
import useMediaQuery from '@cartwhl/ui/hooks/useMediaQuery';
import ChevronLeftIcon from '@cartwhl/ui/icons/ChevronLeftIcon';
import ChevronRightIcon from '@cartwhl/ui/icons/ChevronRightIcon';
import Button from '@cartwhl/ui/input/Button';

export interface ResponsiveSectionLayoutSection<TSlug extends string = string> {
  slug: TSlug;
  label: string;
  mobileTitle?: string;
  content: ReactNode;
  maxWidthClassName?: string;
}

export interface ResponsiveSectionLayoutProps<TSlug extends string = string> {
  sections: ResponsiveSectionLayoutSection<TSlug>[];
  activeSlug?: TSlug;
  navigationLabel: string;
  className?: string;
  desktopNavClassName?: string;
  desktopContentClassName?: string;
  mobileContentClassName?: string;
  mobileBreakpointQuery?: string;
  renderDesktopHeader?: (
    activeSection: ResponsiveSectionLayoutSection<TSlug>,
  ) => ReactNode;
  renderMobileMenuHeader?: () => ReactNode;
  onSelect: (slug: TSlug) => void;
  onMobileBack?: () => void;
}

export default function ResponsiveSectionLayout<TSlug extends string = string>({
  sections,
  activeSlug,
  navigationLabel,
  className,
  desktopNavClassName,
  desktopContentClassName = 'min-w-0 flex-auto',
  mobileContentClassName = 'mx-auto max-w-(--breakpoint-md)',
  mobileBreakpointQuery = '(min-width: 768px)',
  renderDesktopHeader,
  renderMobileMenuHeader,
  onSelect,
  onMobileBack,
}: ResponsiveSectionLayoutProps<TSlug>) {
  const isDesktop = useMediaQuery(mobileBreakpointQuery);

  if (sections.length === 0) {
    return null;
  }

  const activeSection = activeSlug
    ? sections.find((section) => section.slug === activeSlug)
    : undefined;

  if (activeSlug && !activeSection) {
    return null;
  }

  const desktopSection = activeSection ?? sections[0];

  if (!isDesktop) {
    if (!activeSection) {
      const menuHeader = renderMobileMenuHeader?.();

      return (
        <div className={className}>
          {menuHeader ? (
            <div className={mobileContentClassName}>{menuHeader}</div>
          ) : null}
          <ResponsiveSectionMobileMenu
            sections={sections}
            className={`${mobileContentClassName} ${menuHeader ? 'mt-8' : ''}`}
            onSelect={onSelect}
          />
        </div>
      );
    }

    return (
      <div className={className}>
        <div className={mobileContentClassName}>
          <ResponsiveSectionMobileSubpageHeader
            title={activeSection.mobileTitle ?? activeSection.label}
            className="mb-6"
            onBack={onMobileBack}
          />
          {activeSection.content}
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div className="flex gap-4">
        <ResponsiveSectionDesktopNav
          sections={sections}
          activeSlug={desktopSection.slug}
          navigationLabel={navigationLabel}
          className={desktopNavClassName}
          onSelect={onSelect}
        />
        <div className={desktopContentClassName}>
          {renderDesktopHeader?.(desktopSection)}
          {desktopSection.content}
        </div>
      </div>
    </div>
  );
}

interface ResponsiveSectionDesktopNavProps<TSlug extends string = string> {
  sections: ResponsiveSectionLayoutSection<TSlug>[];
  activeSlug?: TSlug;
  navigationLabel: string;
  className?: string;
  onSelect: (slug: TSlug) => void;
}

function ResponsiveSectionDesktopNav<TSlug extends string = string>({
  sections,
  activeSlug,
  navigationLabel,
  className,
  onSelect,
}: ResponsiveSectionDesktopNavProps<TSlug>) {
  return (
    <nav
      aria-label={navigationLabel}
      className={`sticky top-24 mt-1.5 flex w-[180px] flex-none flex-col gap-2 self-start ${className ?? ''}`}
    >
      {sections.map((section) => {
        const isActive = section.slug === activeSlug;

        return (
          <button
            key={section.slug}
            type="button"
            aria-current={isActive ? 'page' : undefined}
            className={`hover:bg-secondary dark:hover:bg-gray-60 flex h-10 w-full items-center rounded-lg px-4 pt-0.5 text-left transition-colors ${
              isActive
                ? 'bg-secondary hover:bg-secondary dark:bg-gray-75 dark:hover:bg-gray-75 cursor-default'
                : ''
            }`}
            onClick={() => onSelect(section.slug)}
          >
            <Text token="14/medium">{section.label}</Text>
          </button>
        );
      })}
    </nav>
  );
}

interface ResponsiveSectionMobileMenuProps<TSlug extends string = string> {
  sections: ResponsiveSectionLayoutSection<TSlug>[];
  className?: string;
  onSelect: (slug: TSlug) => void;
}

function ResponsiveSectionMobileMenu<TSlug extends string = string>({
  sections,
  className,
  onSelect,
}: ResponsiveSectionMobileMenuProps<TSlug>) {
  return (
    <div className={className}>
      <div className="border-primary bg-primary dark:bg-gray-90/30 overflow-hidden rounded-xl border shadow-sm">
        {sections.map((section, index) => (
          <button
            key={section.slug}
            type="button"
            className={`flex w-full items-center gap-3 px-4 py-4 text-left ${
              index > 0 ? 'border-primary border-t' : ''
            }`}
            onClick={() => onSelect(section.slug)}
          >
            <Text token="14/medium">{section.label}</Text>
            <ChevronRightIcon className="text-tertiary ml-auto h-5 w-5 flex-none" />
          </button>
        ))}
      </div>
    </div>
  );
}

interface ResponsiveSectionMobileSubpageHeaderProps {
  title: string;
  className?: string;
  onBack?: () => void;
}

function ResponsiveSectionMobileSubpageHeader({
  title,
  className,
  onBack,
}: ResponsiveSectionMobileSubpageHeaderProps) {
  return (
    <div className={className}>
      <Button
        type="button"
        variant="tertiary"
        icon={<ChevronLeftIcon />}
        onClick={onBack}
        className="-ml-3"
      >
        {title}
      </Button>
    </div>
  );
}
